// @owners { team: patients }
import { Toast, ToastContext } from '@alto/design-system';
// eslint-disable-next-line @alto/prefer-alto-features-shortform -- deep import keeps the @alto/features barrel out of the eager `main` bundle; login is rendered eagerly (see PatientDashboardApp)
import { WelcomePage } from '@alto/features/src/login/components/WelcomePage';
import { useNavigation } from '@alto/navigation';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { clearPostLoginRoute, loginWeb } from '~shared/actions/auth';
import { DEFAULT_ERROR_ALERT_MESSAGE } from '~shared/constants';
import { getIsLoggedIn } from '~shared/features/auth/selectors/getIsAuthorized';
import getErrors from '~shared/features/ui/selectors/getErrors';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { areCookiesDisabled } from '~shared/helpers/getDeviceID';
import { getURLParams } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getAuth from '~shared/selectors/auth/getAuth';
import { Sentry } from '~shared/sentry';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { useShowPostAuthActionSheets } from '~web/features/auth/hooks/useShowPostAuthActionSheets';

export const Welcome = () => {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const { addToast } = useContext(ToastContext);
  const loggedIn = useSelectorShared(getIsLoggedIn);
  const { postLoginRoute } = useSelectorShared(getAuth);
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  const [otherError, setOtherError] = useState<unknown | null>(null);
  const [email, setEmail] = useState<string>('');
  const hasLoggedInRef = useRef(false);
  const hasConsumedPostLoginRouteRef = useRef(false);

  const {
    error: auth0Error,
    loginWithRedirect: auth0loginWithRedirect,
    isAuthenticated: isAuth0Authenticated,
    getIdTokenClaims: getAuth0IDTokenClaims,
    handleRedirectCallback,
    isLoading: auth0Loading,
    user: auth0User,
  } = useAuth0();
  const cookiesDisabled = areCookiesDisabled();
  const loginLoading = useSelectorShared(getLoading).loginLoading;
  const loading = auth0Loading || loginLoading;
  const loginError = useSelectorShared(getErrors).loginError;
  const error = loginError || otherError || cookiesDisabled;
  const customErrorMessage = cookiesDisabled ? 'Please enable cookies and refresh page to continue.' : undefined;

  const onLoginError = useCallback(
    (e: unknown, step?: string) => {
      Sentry.captureException(e, (scope) => {
        scope.setLevel('error');
        scope.setTag('login_step', step ?? 'unknown');
        scope.setContext('Login error', { auth0_id: auth0User?.sub });
        return scope;
      });
      setOtherError(e);
    },
    [auth0User?.sub],
  );

  const auth0Login = async () => {
    trackEvent({ event: EVENTS.LOGIN_CLICKED });
    try {
      setOtherError(null);
      // set params for post login routing
      const next = getURLParams().next;
      const appState = next ? { next } : {};
      // open auth0 login page for user to authenticate
      await auth0loginWithRedirect({ appState });
    } catch (e) {
      onLoginError(e, 'auth0_redirect');
    }
  };

  const getTokenAndLogin = useCallback(async () => {
    if (hasLoggedInRef.current) return;
    hasLoggedInRef.current = true;
    try {
      const claims = await getAuth0IDTokenClaims();
      const idToken = claims?.__raw || '';
      const email = claims?.email || '';
      if (!idToken) {
        trackEvent({
          event: EVENTS.AUTH0_LOGIN_NO_TOKEN,
        });
        return;
      }
      if (!email) {
        trackEvent({
          event: EVENTS.AUTH0_LOGIN_NO_EMAIL,
        });
        return;
      }
      setEmail(email);
      dispatch(loginWeb(idToken));
    } catch (e) {
      onLoginError(e, 'get_token');
      hasLoggedInRef.current = false;
    }
  }, [dispatch, getAuth0IDTokenClaims, onLoginError, trackEvent]);

  const handleAuth0Redirect = useCallback(async () => {
    const params = getURLParams();
    const auth0Redirected = !!(params.code && params.state);

    if (auth0Redirected && !hasLoggedInRef.current) {
      try {
        // Finalize the Auth0 login by exchanging code for tokens
        await handleRedirectCallback();
        // Remove code/state from the URL for cleanliness
        window.history.replaceState({}, document.title, window.location.pathname);
        // Now user is authenticated; get token & login
        await getTokenAndLogin();
      } catch (e) {
        onLoginError(e, 'redirect_callback');
        hasLoggedInRef.current = false;
      }
    } else if (isAuth0Authenticated && !loggedIn && !hasLoggedInRef.current) {
      // cover cases where Auth0 login is already complete
      await getTokenAndLogin();
    }
  }, [getTokenAndLogin, handleRedirectCallback, isAuth0Authenticated, loggedIn, onLoginError]);

  // get ID token after auth0 authentication and use to call our own login endpoint
  useEffect(() => {
    if (auth0Error) {
      Sentry.captureException(auth0Error, (scope) => {
        scope.setLevel('error');
        scope.setTag('login_step', 'auth0_sdk');
        scope.setContext('Login error', { email, auth0_id: auth0User?.sub });
        return scope;
      });
      addToast(<Toast variant="error">{DEFAULT_ERROR_ALERT_MESSAGE}</Toast>);
      hasLoggedInRef.current = false;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect -- auth0 login flow: handleAuth0Redirect reacts to async auth0 SDK state, not a render-derivable value
    handleAuth0Redirect();
  }, [addToast, auth0Error, auth0User?.sub, email, handleAuth0Redirect]);

  const { handleShowPostAuthActionSheets } = useShowPostAuthActionSheets();

  useEffect(() => {
    if (!loggedIn) return;

    // show action sheets for phi preferences or rate shipment
    handleShowPostAuthActionSheets();
  }, [handleShowPostAuthActionSheets, loggedIn]);

  // navigate (useNavigateUnstable) returns a new reference on every URL change; read the latest via a
  // ref so this effect doesn't re-fire on route transitions and bounce the patient mid-flow.
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  });

  useEffect(() => {
    if (!loggedIn) return;

    if (postLoginRoute && !hasConsumedPostLoginRouteRef.current) {
      hasConsumedPostLoginRouteRef.current = true;
      // Clear the stored route and replace (rather than push) the /login history entry. Otherwise the
      // back button returns to this screen, which — with postLoginRoute still set and loggedIn true —
      // immediately re-navigates forward, producing a flicker loop instead of going back (e.g. a token
      // expiry + re-login that lands the patient on a deep checkout/wizard URL).
      dispatch(clearPostLoginRoute());
      navigateRef.current(postLoginRoute, { replace: true });
    } else if (!postLoginRoute && !hasConsumedPostLoginRouteRef.current) {
      hasConsumedPostLoginRouteRef.current = true;
      navigateRef.current('RouteAppTabNavigator', { screen: 'RouteTabHomescreen' });
    }
  }, [dispatch, loggedIn, postLoginRoute]);

  return (
    <WelcomePage
      login={auth0Login}
      loading={loading}
      error={!!error}
      errorMessage={customErrorMessage}
      navigateToOnboarding={() => {
        navigate('RouteOnboardingGeoVerify', { search: '?origin=login' });
      }}
      disableLogin={cookiesDisabled}
    />
  );
};
